import { dateFormatter, dateTimeFormatter } from "_helper/timeFormatter";

/**
 * Value formatting primitives. Each knows exactly one rendering rule;
 * callers choose which applies to which field. Field-name heuristics
 * ("expiry"-ish keys get date-only rendering) are caller policy.
 */

const DATE_ONLY = /^\d{4}-\d{2}-\d{2}$/;
const DATE_TIME = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}/;

export const isDateOnlyLike = (value: unknown): boolean =>
  typeof value === "string" && DATE_ONLY.test(value.trim());

export const isDateTimeLike = (value: unknown): boolean =>
  typeof value === "string" && DATE_TIME.test(value.trim());

/** ISO date-only strings → localized date. Anything else: "—"/passthrough. */
export const formatDateLike = (value: unknown): string => {
  if (isDateOnlyLike(value)) return dateFormatter((value as string).trim());
  return passthrough(value);
};

/** ISO datetime strings → localized date+time. Anything else: "—"/passthrough. */
export const formatDateTimeLike = (value: unknown): string => {
  if (isDateTimeLike(value)) return dateTimeFormatter((value as string).trim());
  return passthrough(value);
};

/** Render date-ish values: date-only as date, datetime as datetime. */
export const formatTemporal = (value: unknown): string => {
  if (isDateOnlyLike(value)) return dateFormatter((value as string).trim());
  if (isDateTimeLike(value)) return dateTimeFormatter((value as string).trim());
  return passthrough(value);
};

export const formatKm = (value: unknown): string =>
  `${Number(value || 0).toLocaleString()} km`;

export const formatCount = (value: unknown, unit: string): string =>
  `${Number(value || 0)} ${unit}`;

export const yesNo = (value: unknown): string => (value ? "Yes" : "No");

/** Null-safe string rendering: null/undefined/"" render as an em-dash. */
export const passthrough = (value: unknown): string => {
  if (value === null || value === undefined || value === "") return "—";
  if (typeof value === "boolean") return yesNo(value);
  if (typeof value === "object") return JSON.stringify(value);
  return String(value);
};
